import { Prose } from '../../components/Prose'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type EquipmentSettings } from '../../api/client'
import type { Profile } from '../../lib/metrics'
import {
  EQUIPMENT_LABEL, MAX_ADDED, PATTERN_LABEL, SPLITS, candidatesFor, coverageGaps, customFromDay,
  editDay, estimatedMinutes, generateProgram, imageUrl, muscleTr, pickKey, prescriptionText,
  splitFit, splitOf,
  type CatalogExercise, type CustomDay, type Equipment, type Pattern, type PlannedExercise,
  type ProgramEdits, type WorkoutDay,
} from '../../lib/program'
import { ExerciseImage } from '../../components/ExerciseImage'
import { AddExercise } from './AddExercise'
import { DayBuilder } from './DayBuilder'
import { ExerciseHistory } from './ExerciseHistory'
import { SessionCard } from './SessionCard'
import { SessionHistory } from './SessionHistory'
import { WorkoutCalendar } from './WorkoutCalendar'
import { TrainingLoad } from './TrainingLoad'
import { BantAyari } from './BantAyari'

const HOME_EQUIPMENT: Equipment[] = [
  'bodyweight', 'bands', 'pullup_bar', 'dumbbell', 'kettlebell', 'ball',
]
const GYM_EQUIPMENT: Equipment[] = [...HOME_EQUIPMENT, 'barbell', 'machine', 'cable']

/**
 * Programım sekmesi de tek parçaydı: bugünkü antrenman, ekipman, iki gün
 * şablonu, hacim tablosu, geçmiş ve bir de "nasıl kuruldu" metni — telefonda
 * 7.4 ekran. Antrenman yaparken lazım olan tek şey en üstteki kart, gerisi
 * ayda bir açılıyor. Bölümlere ayırdık; varsayılan "Bugün".
 */
const SECTIONS = [
  { id: 'bugun', label: 'Bugün' },
  { id: 'program', label: 'Program' },
  { id: 'yuk', label: 'Haftalık yük' },
  { id: 'gecmis', label: 'Geçmiş' },
] as const
type SectionId = (typeof SECTIONS)[number]['id']

export function ProgramTab({ profile }: { profile: Profile }) {
  const [section, setSection] = useState<SectionId>('bugun')
  /**
   * Takvimden seçilen geçmiş gün. null ise bugün.
   *
   * Ayrı bir ekran açmıyoruz: aynı antrenman kartını farklı bir tarihle
   * gösteriyoruz. İkinci bir "geçmiş giriş formu" yazmak, aynı işi iki yerde
   * farklı davranan iki koda bölmek olurdu.
   */
  const [gecmisGun, setGecmisGun] = useState<string | null>(null)
  const qc = useQueryClient()
  const catalog = useQuery({ queryKey: ['exercises'], queryFn: () => api.exercises() })
  const eq = useQuery({ queryKey: ['equipment'], queryFn: api.equipment })
  const [open, setOpen] = useState<string | null>(null)
  const [alts, setAlts] = useState<string | null>(null)
  const [adding, setAdding] = useState<string | null>(null)
  // Kurucu taslağı YEREL: her tuşta sunucuya yazmak, imleç zıplaması ve
  // yarım kalmış bir günün kaydedilmesi demek. "Kaydet"e basınca gidiyor.
  const [building, setBuilding] = useState<{ label: string; draft: CustomDay } | null>(null)

  const save = useMutation({
    mutationFn: (v: {
      trainingPlace: string; equipment: string[]
      picks: Record<string, string>; edits: ProgramEdits; split: string; bandsKg: number[]
    }) => api.saveEquipment(
      v.trainingPlace, v.equipment, v.picks, v.edits, v.split, v.bandsKg),
    // Optimistic: kutu tık anında dolsun. Sunucu cevabını beklersek
    // "tıkladım ama olmadı" hissi oluyor ve program bir an geç güncelleniyor.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['equipment'] })
      const prev = qc.getQueryData<EquipmentSettings>(['equipment'])
      qc.setQueryData<EquipmentSettings>(['equipment'], {
        trainingPlace: v.trainingPlace,
        equipment: [...new Set([...v.equipment, 'bodyweight'])].sort(),
        picks: v.picks,
        edits: v.edits,
        split: v.split,
        bandsKg: v.bandsKg,
      })
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['equipment'], ctx.prev) },
    onSettled: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  })

  if (catalog.isLoading || eq.isLoading) {
    return <div className="card"><p className="sub">Hareket kataloğu yükleniyor…</p></div>
  }

  if (!catalog.data?.length) {
    return (
      <div className="card">
        <h2>Katalog boş</h2>
        <p className="sub">
          Hareket verisi henüz indirilmemiş. Proje klasöründe bir kez şunu çalıştır:
        </p>
        <pre style={{ background: 'var(--chip)', padding: 12, borderRadius: 9, fontSize: 13, overflowX: 'auto' }}>
          node tools/fetch-exercises.mjs
        </pre>
        <Prose className="note">
          ~113 MB indirir (873 hareket ve görselleri), sonra uygulamayı yeniden başlat.
          Kaynak: yuhonas/free-exercise-db, kamu malı.
        </Prose>
      </div>
    )
  }

  const place = eq.data?.trainingPlace ?? 'home'
  const have = eq.data?.equipment ?? ['bodyweight']
  const picks = eq.data?.picks ?? {}
  const edits = eq.data?.edits ?? {}
  const split = splitOf(eq.data?.split)
  const bandsKg = eq.data?.bandsKg ?? []
  const options = place === 'gym' ? GYM_EQUIPMENT : HOME_EQUIPMENT

  const toggle = (item: Equipment) => {
    const next = have.includes(item) ? have.filter((x) => x !== item) : [...have, item]
    save.mutate({ trainingPlace: place, equipment: next, picks, edits, split: split.id, bandsKg })
  }

  const choose = (key: string, slug: string) => {
    // Aynı hareketi tekrar seçmek seçimi kaldırır → üretici kendi kararına döner.
    const next = { ...picks }
    if (next[key] === slug) delete next[key]
    else next[key] = slug
    save.mutate({ trainingPlace: place, equipment: have, picks: next, edits, split: split.id, bandsKg })
    setAlts(null)
  }

  const setEdits = (next: ProgramEdits) =>
    save.mutate({ trainingPlace: place, equipment: have, picks, edits: next, split: split.id, bandsKg })

  /** Üreticinin koyduğu bir kalıbı çıkar / geri koy. */
  const toggleDrop = (label: string, pattern: Pattern) => {
    const cur = edits[label]?.drop ?? []
    const next = cur.includes(pattern) ? cur.filter((x) => x !== pattern) : [...cur, pattern]
    setEdits(editDay(edits, label, { drop: next }))
  }

  /** Elle eklenen bir hareketi listeden çıkar. */
  const removeAdded = (label: string, slug: string) =>
    setEdits(editDay(edits, label, { add: (edits[label]?.add ?? []).filter((s) => s !== slug) }))

  const addExercise = (label: string, slug: string) => {
    setEdits(editDay(edits, label, { add: [...(edits[label]?.add ?? []), slug] }))
    setAdding(null)
  }

  const { days } = generateProgram({
    catalog: catalog.data, equipment: have, picks, edits, split: split.id })
  const fit = splitFit(split, profile.strengthPerWeek || 3)
  const gaps = coverageGaps(days)

  const startBuilding = (day: WorkoutDay) =>
    setBuilding({ label: day.label, draft: edits[day.label]?.custom ?? customFromDay(day) })

  const saveBuilding = () => {
    if (!building) return
    setEdits(editDay(edits, building.label, { custom: building.draft }))
    setBuilding(null)
  }

  return (
    <>
      <div className="segbar" role="tablist" aria-label="Program bölümleri">
        {SECTIONS.map((x) => (
          <button key={x.id} role="tab" aria-selected={section === x.id}
                  onClick={() => {
                    // "Bugün"e elle dokunmak bugüne dönmek demek. Temizlemezsek
                    // takvimden bir gün seçtikten sonra sekmeye basan biri hâlâ
                    // o eski günü görür ve bunu fark etmeden üstüne yazar.
                    if (x.id === 'bugun') setGecmisGun(null)
                    setSection(x.id)
                  }}>
            {x.label}
          </button>
        ))}
      </div>

      {section === 'bugun' &&
        <SessionCard
          /* key: tarih değişince bileşen sıfırdan kurulsun. İçerideki taslak
             bir ref'te tutuluyor ve remount olmazsa önceki günün kaydı yeni
             tarihe yapışır — sessiz ve çok kötü bir hata. */
          key={gecmisGun ?? 'bugun'}
          catalog={catalog.data} equipment={have} bandsKg={bandsKg} picks={picks} edits={edits}
          split={split.id}
          date={gecmisGun ?? undefined}
          onClose={gecmisGun ? () => setGecmisGun(null) : undefined} />}

      {section === 'program' && <>
      <div className="card">
        <h2>Nasıl bölelim?</h2>
        <Prose className="card-h">
          Hepsi aynı sekiz hareket kalıbından kuruluyor; fark kalıpların günlere
          nasıl dağıtıldığı. Doğru seçim zevk meselesi değil, <strong>gün sayısı</strong>
          {' '}meselesi — az günde bölmek her kası haftada bir kez çalıştırır ve
          haftalık hacmi tek seansa yığmak zorunda bırakır.
        </Prose>

        <div className="splitgrid">
          {SPLITS.map((s) => {
            const on = s.id === split.id
            return (
              <button key={s.id} className={on ? 'splitcard on' : 'splitcard'} aria-pressed={on}
                      onClick={() => save.mutate({
                        trainingPlace: place, equipment: have, picks, edits, split: s.id, bandsKg })}>
                <span className="splitcard-name">{s.name}</span>
                <span className="splitcard-days">
                  {s.days.map((d) => d.name).join(' · ')}
                </span>
                <span className="pill" style={{ marginTop: 6 }}>
                  {s.bestDays.join(' veya ')} gün/hafta
                </span>
              </button>
            )
          })}
        </div>

        <Prose className="note">{split.note}</Prose>

        {fit && (
          <div className="warnbox">
            <strong>Gün sayısı uyuşmuyor:</strong> {fit}{' '}
            Ayarlar → “Haftada kaç kuvvet antrenmanı” alanından değiştirebilirsin.
          </div>
        )}
      </div>

      <div className="card">
        <h2>Nerede çalışıyorsun?</h2>
        <Prose className="card-h">
          Program elindeki ekipmandan üretiliyor. Bir şeyi işaretlemezsen o hareket hiç önerilmez —
          yapamayacağın bir programın faydası yok.
        </Prose>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['home', 'gym'] as const).map((k) => (
            <button key={k} className={place === k ? 'primary' : ''}
                    onClick={() => save.mutate({ trainingPlace: k, equipment: have, picks, edits, split: split.id, bandsKg })}>
              {k === 'home' ? '🏠 Evde' : '🏋️ Spor salonu'}
            </button>
          ))}
        </div>

        <h3>Elindeki ekipman</h3>
        <div className="grid g3">
          {options.map((item) => (
            <label className="chk" key={item} style={{ borderBottom: 'none' }}>
              <input type="checkbox" checked={have.includes(item)}
                     disabled={item === 'bodyweight'}
                     onChange={() => toggle(item)} />
              <span>
                <span className="t">{EQUIPMENT_LABEL[item]}</span>
                {item === 'bodyweight' && <><br /><span className="d">Her zaman açık</span></>}
                {item === 'pullup_bar' && <><br /><span className="d">Kapı barı da sayılır</span></>}
              </span>
            </label>
          ))}
        </div>

        {/* Bant listesi yalnızca bant seçiliyken: seçmeyen biri için gereksiz
            bir form, seçen biri içinse kg kutusunun ön koşulu. */}
        {have.includes('bands') && (
          <BantAyari
            bandsKg={bandsKg}
            onKaydet={(next) => save.mutate({
              trainingPlace: place, equipment: have, picks, edits,
              split: split.id, bandsKg: next,
            })} />
        )}
      </div>

      {days.map((day) => {
        const added = day.exercises.filter((e) => e.source === 'added')
        const minutes = estimatedMinutes(day)
        const build = building?.label === day.label ? building : null
        return (
        <div className="card" key={day.label}>
          <h2>
            {day.name ?? `Antrenman ${day.label}`}
            <span className="pill" style={{ marginLeft: 8, verticalAlign: 'middle' }}>{day.label}</span>
            {day.custom
              ? <span className="pill pill-accent" style={{ marginLeft: 8, verticalAlign: 'middle' }}>senin günün</span>
              : day.edited && <span className="pill" style={{ marginLeft: 8, verticalAlign: 'middle' }}>düzenlendi</span>}
          </h2>
          <Prose className="card-h">
            {day.custom
              ? 'Bu günü sen kurdun — üretici bu güne hiç karışmıyor. Sıra, set, tekrar ve '
                + 'dinlenme senin yazdığın gibi.'
              : <>
                  {day.patterns.map((p) => PATTERN_LABEL[p]).join(' · ')} — her kalıptan bir hareket,
                  böylece hiçbir bölge unutulmuyor. Bu bir <strong>öneri</strong>: aşağıdan hareket
                  çıkarabilir, ekleyebilir, muadiliyle değiştirebilirsin.
                </>}
          </Prose>

          {build && (
            <DayBuilder
              day={day}
              draft={build.draft}
              catalog={catalog.data!}
              equipment={have}
              onChange={(draft) => setBuilding({ label: day.label, draft })}
              onCancel={() => setBuilding(null)}
              onSave={saveBuilding}
            />
          )}

          {!build && <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span className="pill">{day.exercises.length} hareket</span>
            <span className="pill">
              ~{minutes} dk
              {minutes > profile.sessionMinutes && ' · ayarladığın süreyi aşıyor'}
            </span>
            {added.length > 0 && <span className="pill">{added.length}/{MAX_ADDED} ek hareket</span>}
          </div>

          {day.exercises.map((p, i) => {
            // Anahtar kalıba göre OLMAZ: elle kurulan günde aynı kalıptan iki
            // hareket olabiliyor ve React aynı anahtarı iki kez görünce listeyi
            // karıştırıyor (gün değiştirince satırlar birbirine karışıyordu).
            const key = p.source === 'auto'
              ? pickKey(day.label, p.pattern)
              : `${day.label}:${p.source}:${i}:${p.exercise.slug}`
            return (
              <ExerciseRow
                key={key}
                p={p}
                // Elle kurulan günde muadil önerisi yok: gün artık üreticinin
                // değil, hareketi değiştirmek "Günü düzenle"den geçiyor.
                candidates={p.source === 'auto' ? candidatesFor(catalog.data!, p.pattern, have) : []}
                picked={p.source === 'auto' ? picks[pickKey(day.label, p.pattern)] : undefined}
                open={open === key}
                showAlts={alts === key}
                onToggle={() => setOpen(open === key ? null : key)}
                onToggleAlts={() => setAlts(alts === key ? null : key)}
                onPick={(slug) => choose(pickKey(day.label, p.pattern), slug)}
                onRemove={day.custom ? undefined : () => p.source === 'added'
                  ? removeAdded(day.label, p.exercise.slug)
                  : toggleDrop(day.label, p.pattern)}
              />
            )
          })}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {!day.custom && (
              <button className={adding === day.label ? '' : 'primary'}
                      style={{ padding: '6px 12px', fontSize: 13 }}
                      disabled={added.length >= MAX_ADDED && adding !== day.label}
                      onClick={() => setAdding(adding === day.label ? null : day.label)}>
                {adding === day.label ? 'Ekleme listesini kapat' : '+ Hareket ekle'}
              </button>
            )}
            <button className={day.custom ? 'primary' : ''}
                    style={{ padding: '6px 12px', fontSize: 13 }}
                    onClick={() => startBuilding(day)}>
              {day.custom ? 'Günü düzenle' : 'Günü sıfırdan kur'}
            </button>
            {day.custom && (
              <button style={{ padding: '6px 12px', fontSize: 13 }}
                      onClick={() => setEdits(editDay(edits, day.label, { custom: undefined }))}>
                Üretilen programa dön
              </button>
            )}
            {!day.custom && day.edited && (
              <button style={{ padding: '6px 12px', fontSize: 13 }}
                      onClick={() => setEdits(editDay(edits, day.label, { drop: [], add: [] }))}>
                Öneriye dön
              </button>
            )}
          </div>

          {adding === day.label && (
            <AddExercise
              catalog={catalog.data!}
              equipment={have}
              inDay={day.exercises.map((e) => e.exercise.slug)}
              count={added.length}
              onPick={(slug) => addExercise(day.label, slug)}
              onClose={() => setAdding(null)}
            />
          )}

          {day.dropped.length > 0 && (
            <div className="warnbox" style={{ marginTop: 12 }}>
              <strong>Çıkardığın kalıplar:</strong>{' '}
              {day.dropped.map((p) => PATTERN_LABEL[p]).join(', ')}. Bu kalıbın çalıştırdığı
              kaslar bu günde hiç yüklenmiyor. Aşağıdaki <strong>Haftalık yük</strong>
              tablosunun "Program" sütunu bunun karşılığını anında gösteriyor.
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {day.dropped.map((p) => (
                  <button key={p} style={{ padding: '3px 9px', fontSize: 12 }}
                          onClick={() => toggleDrop(day.label, p)}>
                    {PATTERN_LABEL[p]} geri koy
                  </button>
                ))}
              </div>
            </div>
          )}

          {day.missing.length > 0 && (
            <div className="warnbox">
              <strong>Ekipman yetersiz:</strong>{' '}
              {day.missing.map((p) => PATTERN_LABEL[p]).join(', ')} için elindeki ekipmanla
              uygun hareket yok. Bu bölge çalışılmadan kalıyor — üstteki listeden ekipman
              eklersen otomatik dolar.
              {day.missing.includes('pull_v') && (
                <>
                  {' '}
                  <br />
                  <span style={{ color: 'var(--ink-2)' }}>
                    Dikey çekme için evde tek gerçek çözüm bir barfiks barı: kapı kasasına
                    takılan tipi 300–500 TL ve sırtın en büyük hareketini açıyor. Bant bu
                    boşluğu dolduramıyor, çünkü yukarıdan aşağı çekmek için tepede bir
                    tutamak gerekiyor.
                  </span>
                </>
              )}
            </div>
          )}
          </>}
        </div>
        )
      })}

      {gaps.length > 0 && (
        <div className="card">
          <h2>Kapsama uyarısı</h2>
          <p className="sub">
            Programın tamamında şu hareket kalıpları hiç yok:{' '}
            <strong>{gaps.map((p) => PATTERN_LABEL[p]).join(', ')}</strong>.
          </p>
          <Prose className="note">
            Bu bir yasak değil, bir fatura. Kalıp listesi (çömelme, menteşe, itme, çekme,
            gövde) keyfi değil: birlikte bütün büyük kas gruplarını ve her iki eklem
            hareketini kapsıyorlar. Biri düşünce o bölge haftalarca hiç yüklenmiyor ve
            bunu genelde ancak bir şey ağrımaya başlayınca fark ediyorsun. Aşağıdaki
            hacim tablosunun "Program" sütununda hangi kasın boşta kaldığı yazıyor.
          </Prose>
        </div>
      )}

      <div className="card">
        <h2>Program nasıl kuruldu</h2>
        <ul className="tight">
          <li><strong>Hareket listesi değil, kalıp listesi.</strong> Her gün çömelme/menteşe, itme, çekme ve gövde içeriyor. Ekipmanın değişince program bozulmuyor, aynı kalıbın başka varyantı geliyor.</li>
          <li><strong>A ve B dönüşümlü.</strong> Haftada 3 gün çalışıyorsan A–B–A, sonraki hafta B–A–B.</li>
          <li><strong>Yüklenebilir hareket önceliklidir.</strong> Aynı kalıpta hem dambıl hem vücut ağırlığı varsa dambıl gelir — dışarıdan ağırlık eklenebilen hareket ilerlemeye daha uygun.</li>
          <li><strong>Seçim senin.</strong> "Alternatifler"den başka bir hareket seçersen program onu hatırlıyor; aynısına tekrar basınca otomatik seçime dönüyor.</li>
          <li><strong>Üretilen program bir öneri, son söz değil.</strong> İstemediğin kalıbı çıkarabilir, güne {MAX_ADDED} hareket ekleyebilirsin. Düzenleme programın kopyası olarak değil, üreticinin üstüne uygulanan fark olarak saklanıyor: ekipmanın değişince ya da katalog güncellenince program kendini yenilemeye devam ediyor, senin kararın da kaybolmuyor.</li>
          <li><strong>Ek hareket kasa göre seçiliyor.</strong> "Hareket ekle" listesi kalıp diliyle değil kas diliyle konuşuyor (göğüs, omuz, arka bacak) — çünkü hareket eklerken insan kalıp değil kas düşünüyor.</li>
          <li><strong>Seviyenin üstü elenir.</strong> Uzman seviyesi hareketler (push press, asılarak bacak kaldırma) sen o seviyeye gelene kadar önerilmez.</li>
        </ul>
        <Prose className="note">
          Hareket verisi ve görseller: yuhonas/free-exercise-db — Unlicense (kamu malı).
          Türkçe anlatımlar bu panele özel yazıldı.
        </Prose>
      </div>
      </>}

      {section === 'yuk' && (
        <TrainingLoad
          catalog={catalog.data} profile={profile} days={days}
          edits={edits} equipment={have}
          // Öneri "Ekle" düğmesi elle eklemenin AYNI yolunu kullanıyor:
          // ikinci bir ekleme mekanizması olsaydı sınırlar (gün başına 4) ve
          // kaydetme davranışı iki yerde ayrı ayrı tutulmak zorunda kalırdı.
          onEkle={(label, slug) =>
            setEdits(editDay(edits, label, { add: [...(edits[label]?.add ?? []), slug] }))} />
      )}

      {section === 'gecmis' && <>
        {/* Takvim önce, liste sonra. Takvim "hangi günü boş geçtim" ve "geçen
            salı ne yaptım" sorularına bakışta cevap veriyor; liste ise son
            antrenmanların dökümü için hâlâ en okunaklısı. */}
        <WorkoutCalendar onPick={(d) => { setGecmisGun(d); setSection('bugun') }} />
        <SessionHistory />
      </>}
    </>
  )
}

function ExerciseRow({
  p, candidates, picked, open, showAlts, onToggle, onToggleAlts, onPick, onRemove,
}: {
  p: PlannedExercise
  candidates: CatalogExercise[]
  picked?: string
  open: boolean
  showAlts: boolean
  onToggle: () => void
  onToggleAlts: () => void
  onPick: (slug: string) => void
  /** Boşsa "Çıkar" düğmesi çizilmiyor — elle kurulan günde geçerli değil. */
  onRemove?: () => void
}) {
  const [hist, setHist] = useState(false)
  const x = p.exercise
  return (
    <div className="day" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <ExerciseImage images={x.images} pattern={p.pattern} alt={x.nameEn}
                       className="exthumb" width={130} height={97} />
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div className="dn">{PATTERN_LABEL[p.pattern]} · {EQUIPMENT_LABEL[x.equipment as Equipment] ?? x.equipment}</div>
          <div className="dt">{x.nameEn}</div>
          {/* Türkçe ad ikinci planda: hareketi tanıtan şey orijinal adı. */}
          <div className="dd" style={{ marginBottom: 6 }}>
            <span style={{ color: 'var(--muted)' }}>{x.nameTr}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="pill pill-accent">{prescriptionText(p)}</span>
            <span className="pill">{p.restSeconds} sn dinlenme</span>
            {picked && p.source === 'auto' && <span className="pill">senin seçimin</span>}
            {p.source === 'added' && <span className="pill pill-accent">sen ekledin</span>}
            {x.primaryMuscles.map((m) => <span className="pill" key={m}>{muscleTr(m)}</span>)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={onToggle}>
              {open ? 'Anlatımı gizle' : 'Nasıl yapılır'}
            </button>
            {candidates.length > 1 && (
              <button style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={onToggleAlts}>
                {showAlts ? 'Alternatifleri gizle' : `Alternatifler (${candidates.length})`}
              </button>
            )}
            {/* Çıkarmak kalıcı bir silme değil: üretici kararının üstüne yazıyor
                ve "Öneriye dön" ile geri alınıyor. O yüzden onay sormuyoruz. */}
            <button style={{ padding: '4px 10px', fontSize: 12.5 }}
                    aria-expanded={hist} onClick={() => setHist(!hist)}>
              {hist ? 'Geçmişi gizle' : 'Geçmiş'}
            </button>
            {onRemove && (
              <button style={{ padding: '4px 10px', fontSize: 12.5, color: 'var(--muted)' }}
                      onClick={onRemove}>
                Çıkar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Seçenekleri sırayla dolaştırmak yerine hepsini gösteriyoruz: hangi
          hareketin ne olduğu görselden anlaşılıyor, seçim tek dokunuş. */}
      {showAlts && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--grid)', paddingTop: 12 }}>
          <div className="alt-grid">
            {candidates.map((c) => {
              const active = c.slug === x.slug
              return (
                <button
                  key={c.slug}
                  className={active ? 'alt on' : 'alt'}
                  aria-pressed={active}
                  onClick={() => onPick(c.slug)}
                >
                  <ExerciseImage images={c.images} pattern={c.pattern} />
                  <span className="alt-name">{c.nameEn}</span>
                  <span className="alt-sub">{EQUIPMENT_LABEL[c.equipment as Equipment] ?? c.equipment}</span>
                </button>
              )
            })}
          </div>
          <Prose className="note">
            Seçtiğin hareket bu kalıpta kalıcı olarak kullanılır. Seçili olana tekrar
            basarsan otomatik seçime dönersin.
          </Prose>
        </div>
      )}

      {hist && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--grid)', paddingTop: 12 }}>
          <ExerciseHistory slug={x.slug} unit={p.unit} />
        </div>
      )}

      {open && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--grid)', paddingTop: 12 }}>
          <ol className="tight" style={{ fontSize: 13.5 }}>
            {x.instructions.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          {!x.instructionsAreTurkish && (
            <Prose className="note">Bu hareketin anlatımı henüz çevrilmedi, orijinal metin gösteriliyor.</Prose>
          )}
          {x.images[1] && (
            <img src={imageUrl(x.images[1])} alt={`${x.nameEn} — bitiş`} loading="lazy"
                 style={{ marginTop: 10, borderRadius: 9, maxWidth: 260, width: '100%' }} />
          )}
        </div>
      )}
    </div>
  )
}
